// src/components/DocumentReview.js
//
// SECTION 543 (2026-09-30). The document review on every document the platform produces - spelling, grammar, facts,
// prose. Bob: "every document this platform produces is to have the same proof reading capabilities".
//
// It runs by itself. The record checks (src\data\documentReview.js) run the moment a document is shown; the language
// review (the documentReview callable) runs once the text has been still for a moment, once per distinct text. It
// REPORTS: each finding quotes the document, says what is wrong, and - for spelling and grammar - gives the corrected
// words. The document is corrected at its source (the claim's fields, the letter's inputs), never behind the reader.
import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { localReview, mergeFindings, summary, textKey } from '../data/documentReview';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const LABEL = { leak: 'Blank or code', form: 'Form', fact: 'Fact', prose: 'Prose', spelling: 'Spelling', grammar: 'Grammar' };
const CACHE = new Map();   // textKey -> server findings, for this browser session

export default function DocumentReview({ text, facts, kind, record, title, today }) {
  const body = String(text || '');
  const key = useMemo(() => textKey(body), [body]);
  const local = useMemo(() => localReview(body, facts, { today }), [body, facts, today]);
  const [server, setServer] = useState(CACHE.has(key) ? CACHE.get(key) : null);
  const [state, setState] = useState(CACHE.has(key) ? 'done' : 'idle');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (body.trim().length < 20) { setState('idle'); return undefined; }
    if (CACHE.has(key)) { setServer(CACHE.get(key)); setState('done'); setErr(''); return undefined; }
    setServer(null); setState('waiting'); setErr('');
    let dead = false;
    const timer = setTimeout(async () => {
      setState('running');
      try {
        const call = httpsCallable(getFunctions(), 'documentReview');
        const r = await call({ text: body, kind: kind || 'other', record: record || '' });
        const f = (r && r.data && r.data.findings) || [];
        CACHE.set(key, f);
        if (!dead) { setServer(f); setState('done'); }
      } catch (e) {
        if (!dead) { setErr((e && e.message) || 'The language review did not answer.'); setState('failed'); }
      }
    }, 1500);
    return () => { dead = true; clearTimeout(timer); };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps

  const all = mergeFindings(local.findings, server, body);
  const sum = summary(all);
  const pending = state === 'waiting' || state === 'running';
  const tone = pending && !all.length ? C.dim : sum.tone === 'ok' ? C.ok : sum.tone === 'bad' ? C.bad : C.accent;
  const status = pending ? 'Spelling, grammar and prose: reviewing…'
    : state === 'failed' ? 'Spelling, grammar and prose: NOT reviewed - ' + err
    : state === 'done' ? 'Spelling, grammar and prose: reviewed.' : '';

  return (
    <details open={all.some(f => f.severity === 'fix')} style={{ border: '1px solid ' + tone, borderLeft: '3px solid ' + tone, background: C.panel, borderRadius: 6, padding: '8px 12px', margin: '8px 0' }}>
      <summary style={{ cursor: 'pointer', fontSize: 12.5, color: C.text }}>
        <b>Document review{title ? ' - ' + title : ''}</b>{' '}
        <span style={{ color: tone, fontWeight: 700 }}>{pending && !all.length ? 'reviewing…' : sum.line}</span>
      </summary>
      <div style={{ fontSize: 11, color: C.dim, margin: '6px 0' }}>
        Checked against the record: {local.ran.join(', ')}. {status}
      </div>
      {all.length ? all.map((f, i) => (
        <div key={i} style={{ fontSize: 12, padding: '5px 0', borderTop: '1px solid #16283D', color: C.text }}>
          <span style={{ color: f.severity === 'fix' ? C.bad : C.accent, fontWeight: 700 }}>{LABEL[f.type] || f.type}</span>
          {f.excerpt ? <span> - “<span style={{ background: 'rgba(232,160,32,0.15)' }}>{f.excerpt}</span>”</span> : null}
          {f.suggestion ? <span> → “<span style={{ color: C.ok }}>{f.suggestion}</span>”</span> : null}
          <div style={{ color: C.dim, marginTop: 2 }}>{f.note}</div>
        </div>
      )) : (!pending ? <div style={{ fontSize: 12, color: C.ok }}>Nothing found.</div> : null)}
    </details>
  );
}
